<script setup lang="ts">
/**
 * 전역 PreSet 모달 런처 (FullLayout 에 마운트).
 * 어느 메뉴 페이지에서든 preSetStore.openModal() 호출 시 모달이 그 페이지 위에 노출됨.
 *
 * - select: store 에 selection 저장 후 /tasks/new 로 라우팅. NewTaskPage 가 store 의 selection 을 감지해 docgen 모드로 진입.
 * - skip:   모달 닫기만, 라우팅 없음 (현재 페이지 유지).
 * - 라우트 변경 시 모달 자동 close.
 */
import { computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { usePreSetStore } from '../stores/preSetStore';
import PreSetModal from './PreSetModal.vue';
import type { PreSetSelection } from '../types/preset.types';
import { isPreSetSupported, isSg } from '../../../jurisdictionResolver';

const router = useRouter();
const route = useRoute();
const preSetStore = usePreSetStore();
const { isModalOpen } = storeToRefs(preSetStore);

// PreSet 활성 조건 (MY 모든 환경 / SG local·dev 환경) — jurisdictionResolver 가 한 곳에서 관리.
const isPreSetEnabled = computed(() => isPreSetSupported());

// 비지원 jurisdiction/환경 에서 실수로 openModal 호출 시 즉시 닫고 기존 라우팅으로 fallback.
// SG 는 카드 메뉴 페이지(/tasks/new) 위에서만 PreSet 모달이 노출되어야 하므로,
//   다른 페이지에서 openModal 호출된 경우 라우팅. NewTaskPage onMounted 가 모달 재오픈.
watch(isModalOpen, (open) => {
  if (!open) return;
  if (!isPreSetEnabled.value) {
    preSetStore.closeModal();
    router.push('/tasks/new');
    return;
  }
  if (isSg() && route.path !== '/tasks/new') {
    preSetStore.closeModal();
    router.push('/tasks/new');
  }
});

// 라우트 이동 시 모달 자동 close (사용자가 모달 열린 상태에서 다른 메뉴로 갈 때)
watch(() => route.fullPath, () => {
  if (isModalOpen.value) preSetStore.closeModal();
});

function handleSelect(selection: PreSetSelection) {
  preSetStore.confirmSelection(selection);
  preSetStore.closeModal();
  // NewTaskPage 가 store.selectedPreSet 을 감지해 docgen 모드로 자동 진입.
  // 같은 라우트 (/tasks/new) 위에서 호출된 경우에도 NewTaskPage 가 재마운트되도록 _refresh 추가
  // (FullLayout 의 routerViewKey 가 _refresh 변경 시 RouterView 를 remount)
  router.push({ path: '/tasks/new', query: { _refresh: String(Date.now()) } });
}

function handleSkip() {
  preSetStore.closeModal();
  // 현재 페이지 유지 (라우팅 없음)
}
</script>

<template>
  <PreSetModal
    v-if="isPreSetEnabled"
    v-model="isModalOpen"
    @select="handleSelect"
    @skip="handleSkip"
  />
</template>
